import { useTranslation } from '@ufc/core';
import { cn, Input, Edit2, Button } from '@ufc/uikit';
import type { WeighDirection } from '../../domain/types';

interface ManualOrderEntryCardProps {
  value: string;
  direction: WeighDirection;
  onChange: (value: string) => void;
  onDirectionChange: (direction: WeighDirection) => void;
  className?: string;
}

function getManualEntryTitle(direction: WeighDirection): string {
  if (direction === 'inbound') {
    return 'weighbridgeOps.queue.manualInboundEntry';
  }
  return 'weighbridgeOps.queue.manualEntry';
}

function getManualEntryPlaceholder(direction: WeighDirection): string {
  if (direction === 'inbound') {
    return 'weighbridgeOps.queue.manualInboundPlaceholder';
  }
  return 'weighbridgeOps.queue.manualPlaceholder';
}

export function ManualOrderEntryCard({
  value,
  direction,
  onChange,
  onDirectionChange,
  className,
}: ManualOrderEntryCardProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-md ring-1 ring-inset transition-colors',
            value
              ? 'bg-primary/10 text-primary ring-primary/30 dark:bg-primary/20'
              : 'bg-muted text-muted-foreground ring-border/50'
          )}
        >
          <Edit2 className="h-3.5 w-3.5" />
        </div>
        <div className="flex flex-col gap-0.5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground">
            {t('weighbridgeOps.queue.manualEntry', {
              defaultValue: 'Manual Entry',
            })}
          </h4>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant={direction === 'outbound' ? 'default' : 'outline'}
          size="sm"
          className="h-8 text-xs"
          onClick={() => onDirectionChange('outbound')}
        >
          {t('weighbridgeOps.queue.outbound')}
        </Button>
        <Button
          type="button"
          variant={direction === 'inbound' ? 'default' : 'outline'}
          size="sm"
          className="h-8 text-xs"
          onClick={() => onDirectionChange('inbound')}
        >
          {t('weighbridgeOps.queue.inbound')}
        </Button>
      </div>
      <span className="text-xs font-medium text-muted-foreground">
        {t(getManualEntryTitle(direction))}
      </span>
      <Input
        className={cn(
          'h-9 w-full font-mono text-sm shadow-sm transition-colors bg-background dark:bg-background/50 dark:border-border/80 dark:focus-visible:border-primary/60',
          value &&
            'border-primary/40 bg-primary/5 focus-visible:ring-primary/50 dark:bg-primary/20 dark:border-primary/50 dark:focus-visible:ring-primary/60'
        )}
        placeholder={t(getManualEntryPlaceholder(direction), {
          defaultValue:
            direction === 'inbound'
              ? 'Enter shipment number...'
              : 'Enter order number...',
        })}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
